<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from "../authService";
  import { t } from '../i18nStore';

  let categories = [];
  let isLoading = true;
  let error = '';
  let successMessage = '';
  let isNormalizing = false;

  // Form State
  let isEditing = false;
  let currentId = null;
  let currentName = '';
  let currentNameZh = '';
  let currentNameJa = '';
  let currentKey = '';

  onMount(fetchCategories);

  async function fetchCategories() {
    isLoading = true;
    try {
      const response = await fetch(getApiUrl('/categories'));
      if (!response.ok) throw new Error('Could not load categories.');
      categories = await response.json();
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function startEdit(category) {
    isEditing = true;
    currentId = category._id;
    currentName = category.name || category.nameEn || '';
    currentNameZh = category.nameZh || '';
    currentNameJa = category.nameJa || '';
    currentKey = category.key || '';
    successMessage = '';
    error = '';
  }

  function cancelEdit() {
    isEditing = false;
    currentId = null;
    currentName = '';
    currentNameZh = '';
    currentNameJa = '';
    currentKey = '';
  }

  async function handleSubmit() {
    if (!currentName.trim()) {
      error = 'Category name cannot be empty.';
      return;
    }
    
    const payload = {
      name: currentName.trim(),
      nameEn: currentName.trim(),
      nameZh: currentNameZh.trim() || undefined,
      nameJa: currentNameJa.trim() || undefined,
      key: currentKey.trim() || undefined,
    };
    let url = getApiUrl('/admin/categories');
    let method = 'POST';

    if (isEditing) {
      url = getApiUrl(`/admin/categories/${currentId}`);
      method = 'PUT';
    }

    try {
      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
          const res = await response.json();
          throw new Error(res.error || 'Operation failed.');
      }
      
      successMessage = isEditing ? 'Category updated successfully.' : 'Category added successfully.';
      cancelEdit();
      await fetchCategories();
    } catch (e) {
      error = e.message;
    }
  }

  async function handleDelete(id) {
    if (!confirm('Delete this category?')) return;
    try {
      const response = await authFetch(getApiUrl(`/admin/categories/${id}`), { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete.');
      await fetchCategories();
    } catch (e) {
      alert(e.message);
    }
  }

  async function handleNormalize() {
    if (!confirm($t('admin.category_normalize_confirm'))) return;
    isNormalizing = true;
    error = '';
    successMessage = '';
    try {
      const response = await authFetch(getApiUrl('/admin/categories/normalize'), {
        method: 'POST',
      });
      if (!response.ok) {
        const res = await response.json();
        throw new Error(res.error || 'Normalization failed.');
      }
      const res = await response.json();
      successMessage = `${$t('admin.category_normalized_success')} (${res.updatedCategories} categories, ${res.updatedPromotions} promotions updated)`;
      await fetchCategories();
    } catch (e) {
      error = e.message;
    } finally {
      isNormalizing = false;
    }
  }
</script>

<div class="maintenance-container">
  <div class="header-row">
    <h2>{$t('admin.categories')}</h2>
    <button type="button" class="normalize-btn" on:click={handleNormalize} disabled={isNormalizing}>
      {isNormalizing ? 'Normalizing...' : `⚡ ${$t('admin.category_normalize_btn')}`}
    </button>
  </div>
  
  <div class="grid-layout">
    <!-- Left Column: Form -->
    <div class="action-column">
        <div class="card form-section">
            <h3>{isEditing ? 'Edit Category' : 'Add New Category'}</h3>
            <form on:submit|preventDefault={handleSubmit}>
                <div class="form-group">
                    <label for="cat-name">{$t('admin.category_name_en')} *</label>
                    <input type="text" id="cat-name" bind:value={currentName} placeholder="e.g. Dining" required />
                </div>
                <div class="form-group">
                    <label for="cat-name-zh">{$t('admin.category_name_zh')}</label>
                    <input type="text" id="cat-name-zh" bind:value={currentNameZh} placeholder="e.g. 餐飲" />
                </div>
                <div class="form-group">
                    <label for="cat-name-ja">{$t('admin.category_name_ja')}</label>
                    <input type="text" id="cat-name-ja" bind:value={currentNameJa} placeholder="e.g. グルメ" />
                </div>
                <div class="form-group">
                    <label for="cat-key">{$t('admin.category_key')}</label>
                    <input type="text" id="cat-key" bind:value={currentKey} placeholder="e.g. dining (optional)" />
                </div>
                <div class="form-actions">
                    {#if isEditing}
                    <button type="button" class="secondary" on:click={cancelEdit}>Cancel</button>
                    {/if}
                    <button type="submit" class="primary">{isEditing ? 'Save Changes' : 'Add Category'}</button>
                </div>
            </form>
            {#if successMessage}<p class="success">{successMessage}</p>{/if}
            {#if error}<p class="error">{error}</p>{/if}
        </div>
    </div>

    <!-- Right Column: List -->
    <div class="card list-section">
      <h3>Existing Categories ({categories.length})</h3>
      {#if isLoading}
        <p>Loading...</p>
      {:else if categories.length === 0}
        <p>No categories found.</p>
      {:else}
        <div class="table-wrapper">
          <table class="category-table">
            <thead>
              <tr>
                <th>English</th>
                <th>繁體中文</th>
                <th>日本語</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {#each categories as category (category._id)}
                <tr>
                  <td>
                    <span class="main-name">{category.name}</span>
                    {#if category.key}<small class="key-badge">{category.key}</small>{/if}
                  </td>
                  <td>{category.nameZh || '-'}</td>
                  <td>{category.nameJa || '-'}</td>
                  <td>
                    <div class="item-actions">
                      <button class="edit-btn" on:click={() => startEdit(category)}>Edit</button>
                      <button class="delete-btn" on:click={() => handleDelete(category._id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .maintenance-container { max-width: 1200px; margin: 2rem auto; padding: 0 1rem; }
  .header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
  .normalize-btn { background-color: #6f42c1; color: white; padding: 0.6rem 1.2rem; border-radius: 6px; border: none; cursor: pointer; font-weight: bold; }
  .normalize-btn:hover { background-color: #5a32a3; }
  .normalize-btn:disabled { opacity: 0.6; cursor: not-allowed; }

  .grid-layout { display: grid; grid-template-columns: 1fr; gap: 2rem; }
  @media(min-width: 840px) { .grid-layout { grid-template-columns: 360px 1fr; } }
  
  .card { background: #fff; padding: 1.5rem; border-radius: 8px; box-shadow: 0 2px 5px rgba(0,0,0,0.05); margin-bottom: 1.5rem; }
  h2, h3 { margin-top: 0; color: #0056b3; }
  
  .form-group { margin-bottom: 1rem; text-align: left; }
  label { display: block; margin-bottom: 0.4rem; font-weight: 500; font-size: 0.9rem; color: #333; }
  input { width: 100%; padding: 0.65rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; font-size: 0.95rem; }
  
  .form-actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1.2rem; }
  button { padding: 0.6rem 1.2rem; border: none; border-radius: 4px; cursor: pointer; font-size: 0.95rem; color: white; }
  .primary { background-color: #007bff; width: 100%; font-weight: bold; }
  .secondary { background-color: #6c757d; }
  
  .table-wrapper { max-height: 650px; overflow-y: auto; }
  .category-table { width: 100%; border-collapse: collapse; text-align: left; font-size: 0.95rem; }
  .category-table th { padding: 0.75rem 0.5rem; border-bottom: 2px solid #dee2e6; color: #495057; font-weight: 600; }
  .category-table td { padding: 0.75rem 0.5rem; border-bottom: 1px solid #eee; vertical-align: middle; }
  .main-name { font-weight: 600; display: block; }
  .key-badge { display: inline-block; font-size: 0.75rem; background: #e9ecef; color: #495057; padding: 2px 6px; border-radius: 4px; margin-top: 2px; }
  
  .item-actions { display: flex; gap: 0.4rem; }
  .edit-btn { background-color: #ffc107; color: #333; padding: 4px 10px; font-size: 0.85rem; }
  .delete-btn { background-color: #dc3545; padding: 4px 10px; font-size: 0.85rem; }
  
  .success { color: #28a745; text-align: center; margin-top: 0.5rem; font-weight: 500; }
  .error { color: #dc3545; text-align: center; margin-top: 0.5rem; font-weight: 500; }
</style>